import ExportButton from '../../../components/ExportButton';
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiNavigation,
  FiSearch,
} from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlineRestartAlt } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import CostCenterFilterDropdown from "../../../components/CostCenterFilterDropdown";
import CustomerFilterDropdown from "../../../components/CustomerFilterDropdown";
import DateFilterDropdown from "../../../components/DateFilterDropdown";
import DriverFilterDropdown from "../../../components/DriverFilterDropdown";
import NewTableComponent from "../../../components/NewTableComponent";
import Notification from "../../../components/Notification";
import UserAvatar from "../../../components/UserAvatar";
import VehicleFilterDropdown from "../../../components/VehicleFilterDropdown";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import NoPermissionModal from "../../../components/modals/NoPermissionModal";
import { apiService } from "../../../services/apiService";
import { fetchTripOverview } from "../../../store/Slices/TripSlice";
import {
  fetchCostCenterDropdown,
  fetchCustomerDropdown,
  fetchUsersDropdown,
  fetchVehiclesDropdown,
} from "../../../store/Slices/dropdownSlice";
import "../../../styles/Trip.css";
import "../../../styles/TripsList.css";
import {
  AppMainModules,
  TripPermission,
  TripSubModules,
  TripsStageEnum,
} from "../../../utils/app-constant";
import {
  canViewTripRouteJourney,
  hasPermission,
  hasTripPermission,
  isSuperAdminRole,
} from "../../../utils/helper";
import { plusIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import MobileListingFilters from "../../../components/MobileListingFilters";
import CreateTripDrawer from "./CreateTripDrawer";
import TripsListHeader from "./TripsListHeader";
import TripsListStats from "./TripsListStats";
import { useTripListingPdfExport, useTripListingExcelExport } from "./useTripListingPdfExport";
import {
  formatNumberValue,
  formatFuelValue,
  formatVehicleTypeValue,
  calculateTatDays,
  calculateTotalFuel,
  calculateBalanceFuel,
  calculateKmPerLiter,
  getLoadingPoint,
  getDecantingPoint,
  getTripRevenue,
  getFuelExpense,
  getInsuranceAmount,
  getShortageAmount,
  getTripExpense,
  getPettyCashAllocated,
  getCreatedUser,
  getCreatedDate,
} from "./tripFieldHelpers";
import "../../../styles/Services.css";

const sanitizeDriverName = (name) => {
  if (!name || typeof name !== "string") return "";
  // Strip trailing phone numbers in parentheses if present, e.g. "Driver (923450036410)" -> "Driver"
  const cleaned = name.replace(/\s*\(\+?\d+\)\s*$/, "").trim();
  return cleaned || "Driver";
};

const mapApiTripToTableItem = (
  item,
  contractVehicleTypeTitle = null,
  userMap = null,
) => {
  if (!item) return null;

  const tripId = item?.trip_id || `TR-${item?.id || ""}`;
  const customerName =
    item?.customer?.name || `Customer #${item?.customer_id || "-"}`;
  const vehicleReg =
    item?.vehicle?.registration_number ||
    item?.vehicle?.registration_number_slug ||
    `VE-${item?.vehicle_id || "-"}`;

  const vehicleType =
    contractVehicleTypeTitle ||
    item?.vehicle?.vehicle_type?.title ||
    item?.vehicle_type?.title ||
    item?.vehicle?.make?.title ||
    (item?.vehicle_type_id
      ? `Vehicle Type #${item.vehicle_type_id}`
      : "Vehicle");

  // The trips API stamps start_at/expected_end_at with a trailing "Z" even though
  // the value is really local (server) wall-clock time, not true UTC (confirmed via
  // network inspection: "2026-07-31T19:38:00.000Z" is meant to display as
  // 31-Jul-2026 07:38 PM, not as the UTC->local converted 01-Aug-2026 12:38 AM).
  // So read the literal date/time digits straight out of the string and never run
  // them through any UTC/local timezone conversion.
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const match = String(dateStr)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return String(dateStr);
    const [, year, monthNum, day, hour, minute] = match;
    const parsed = moment({
      year: Number(year),
      month: Number(monthNum) - 1,
      day: Number(day),
      hour: Number(hour),
      minute: Number(minute),
    });
    return parsed.isValid() ? parsed.format("DD-MMM-YYYY hh:mm A") : String(dateStr);
  };

  const startDate = formatDate(item?.start_at);
  const endDate = formatDate(item?.expected_end_at);

  const isFinished = [
    "closed",
    "completed",
    "complete",
    "driver_completed",
  ].includes(String(item?.status || "").trim().toLowerCase());

  let progress = 0;
  const dist = parseFloat(item?.distance || 0);
  const cov = parseFloat(item?.covered_distance || 0);
  if (isFinished) {
    progress = 100;
  } else if (dist > 0) {
    progress = Math.min(Math.round((cov / dist) * 100), 100);
  } else if (item?.status === "in_transit") {
    progress = 50;
  }

  const rawDriverName =
    item?.primary_driver_name ||
    item?.primary_driver?.name ||
    item?.primary_driver?.username ||
    item?.primary_driver?.full_name ||
    (item?.primary_driver?.first_name
      ? `${item.primary_driver.first_name} ${item.primary_driver.last_name || ""}`.trim()
      : null) ||
    item?.driver_name ||
    item?.driver?.name ||
    item?.driver?.username ||
    item?.driver?.full_name ||
    (item?.driver?.first_name
      ? `${item.driver.first_name} ${item.driver.last_name || ""}`.trim()
      : null) ||
    item?.vehicle?.primary_driver?.name ||
    item?.vehicle?.primary_driver?.username ||
    item?.vehicle?.driver?.name ||
    item?.vehicle?.driver?.username ||
    null;

  const mappedDriverName =
    userMap && item?.primary_driver_id
      ? userMap.get(String(item.primary_driver_id))
      : null;

  const hasDriver = Boolean(
    item?.primary_driver_id ||
    item?.primary_driver_contact ||
    item?.primary_driver ||
    item?.driver_name ||
    item?.driver,
  );

  const initialDriverName =
    rawDriverName || mappedDriverName || (hasDriver ? "Driver" : "—");
  const driverName = sanitizeDriverName(initialDriverName);

  const rawContact =
    item?.primary_driver_contact ||
    item?.driver_contact ||
    item?.primary_driver?.phone ||
    item?.primary_driver?.mobile ||
    item?.primary_driver?.contact;
  const driverPhone = rawContact
    ? String(rawContact).startsWith("+")
      ? String(rawContact)
      : `+${rawContact}`
    : "—";
  const driverImage =
    item?.primary_driver?.image ||
    item?.primary_driver?.avatar ||
    item?.primary_driver_image ||
    null;

  const origin = item?.dispatch_point || "Dispatch Point";
  const destination = item?.decanting_point || "Decanting Point";

  const registrationNumber =
    item?.vehicle?.registration_number ||
    item?.vehicle?.registration_number_slug ||
    vehicleReg;

  return {
    id: tripId,
    rawId: item?.id,
    customer: customerName,
    origin,
    destination,
    progress,
    eta: item?.expected_end_at
      ? `End: ${formatDate(item.expected_end_at)}`
      : "N/A",
    startDate,
    createdDate: formatDate(item?.created_at),
    endDate,
    vehicleType,
    registrationNumber,
    vehicleModel: vehicleType,
    vehicleId: registrationNumber,
    driverName,
    driverPhone,
    driverImage,
    status: item?.status || "new",
    stage: item?.stage,
    has_invoice: !!item?.has_invoice,
    raw: item,
  };
};

const renderTripStatusBadge = (statusValue) => {
  const rawStatus = String(statusValue || "")
    .trim()
    .toLowerCase();

  const statusConfig = {
    new: { label: "New", bg: "#EFF6FF", color: "#1D4ED8", border: "#BFDBFE" },
    created: {
      label: "Created",
      bg: "#EFF6FF",
      color: "#1D4ED8",
      border: "#BFDBFE",
    },
    planned: {
      label: "Planned",
      bg: "#EFF6FF",
      color: "#1D4ED8",
      border: "#BFDBFE",
    },
    active: {
      label: "Active",
      bg: "#ECFDF5",
      color: "#047857",
      border: "#A7F3D0",
    },
    in_transit: {
      label: "In Transit",
      bg: "#FEF3C7",
      color: "#D97706",
      border: "#FDE68A",
    },
    moving: {
      label: "In Transit",
      bg: "#FEF3C7",
      color: "#D97706",
      border: "#FDE68A",
    },
    driver_completed: {
      label: "Driver Completed",
      bg: "#E0E7FF",
      color: "#4338CA",
      border: "#C7D2FE",
    },
    completed: {
      label: "Completed",
      bg: "#DCFCE7",
      color: "#15803D",
      border: "#BBF7D0",
    },
    closed: {
      label: "Closed",
      bg: "#F1F5F9",
      color: "#475569",
      border: "#E2E8F0",
    },
    late: { label: "Late", bg: "#FFE4E6", color: "#E11D48", border: "#FECDD3" },
    delayed: {
      label: "Delayed",
      bg: "#FFE4E6",
      color: "#E11D48",
      border: "#FECDD3",
    },
    cancelled: {
      label: "Cancelled",
      bg: "#FEE2E2",
      color: "#991B1B",
      border: "#FECACA",
    },
    pending: {
      label: "Pending",
      bg: "#FEF3C7",
      color: "#D97706",
      border: "#FDE68A",
    },
  };

  const config = statusConfig[rawStatus] || {
    label: rawStatus
      ? rawStatus.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "—",
    bg: "#F1F5F9",
    color: "#475569",
    border: "#E2E8F0",
  };

  return (
    <span
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        display: "inline-block",
        textAlign: "center",
        whiteSpace: "nowrap",
        fontFamily: "Roboto, sans-serif",
        lineHeight: "1.4",
      }}
    >
      {config.label}
    </span>
  );
};

const renderTripStageBadge = (stageValue) => {
  const rawStage = String(stageValue || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");

  const stageConfig = {
    created: {
      label: "Created",
      bg: "#EFF6FF",
      color: "#1D4ED8",
      border: "#BFDBFE",
    },
    trip_stops: {
      label: "Trip Stops",
      bg: "#EEF2FF",
      color: "#4F46E5",
      border: "#C7D2FE",
    },
    checklists_and_allocations: {
      label: "Checklists & Allocations",
      bg: "#F3E8FF",
      color: "#7E22CE",
      border: "#E9D5FF",
    },
    in_transit: {
      label: "In Transit",
      bg: "#FEF3C7",
      color: "#D97706",
      border: "#FDE68A",
    },
    correction: {
      label: "Correction",
      bg: "#FEE2E2",
      color: "#DC2626",
      border: "#FECACA",
    },
    unverified: {
      label: "Unverified",
      bg: "#FFF7ED",
      color: "#C2410C",
      border: "#FFEDD5",
    },
    verified: {
      label: "Verified",
      bg: "#ECFDF5",
      color: "#047857",
      border: "#A7F3D0",
    },
    completed: {
      label: "Completed",
      bg: "#DCFCE7",
      color: "#15803D",
      border: "#BBF7D0",
    },
    closed: {
      label: "Closed",
      bg: "#F1F5F9",
      color: "#475569",
      border: "#E2E8F0",
    },
  };

  const config = stageConfig[rawStage] || {
    label: rawStage
      ? rawStage.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      : "—",
    bg: "#F8FAFC",
    color: "#64748B",
    border: "#CBD5E1",
  };

  return (
    <span
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        padding: "4px 10px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: "600",
        display: "inline-block",
        textAlign: "center",
        whiteSpace: "nowrap",
        fontFamily: "Roboto, sans-serif",
        lineHeight: "1.4",
      }}
    >
      {config.label}
    </span>
  );
};

const getTripDetailsRoute = (trip = {}) => {
  const tripId = trip?.rawId || trip?.raw?.id || trip?.id;
  if (!tripId) return "/trips-list";

  const stage = String(trip?.stage || trip?.raw?.stage || "")
    .trim()
    .toLowerCase();
  const status = String(trip?.status || trip?.raw?.status || "")
    .trim()
    .toLowerCase();

  if (stage === "created" || stage === "planned" || stage === "planning") {
    return `/trips-list/planning/${tripId}`;
  }

  if (
    stage === TripsStageEnum.TripStops ||
    stage === "trip_stops" ||
    stage === "trip-stops" ||
    stage === "trip-stop" ||
    stage === "loading-dispatch" ||
    stage === "loading"
  ) {
    return `/trips-list/loading-dispatch/${tripId}`;
  }

  if (
    stage === TripsStageEnum.ChecklistsAndAllocations ||
    stage === "checklists_and_allocations" ||
    stage === "checklists-and-allocations" ||
    stage === "ready-dispatch" ||
    stage === "ready-to-dispatch" ||
    stage === "ready_to_dispatch"
  ) {
    return `/trips-list/ready-dispatch/${tripId}`;
  }

  if (
    stage === TripsStageEnum.InTransit ||
    stage === "in_transit" ||
    stage === "in-transit" ||
    stage === TripsStageEnum.Correction ||
    stage === "correction" ||
    status === "in_transit" ||
    status === "in-transit"
  ) {
    return `/trips-list/in-transit/${tripId}`;
  }

  if (
    stage === TripsStageEnum.Unverified ||
    stage === "unverified" ||
    stage === TripsStageEnum.Verified ||
    stage === "verified" ||
    stage === TripsStageEnum.Closed ||
    stage === "closed" ||
    stage === TripsStageEnum.Completed ||
    stage === "completed" ||
    stage === "approved" ||
    status === "closed" ||
    status === "completed"
  ) {
    return `/trips-list/trip-completed/${tripId}`;
  }

  // Default fallback: Trip Planning screen
  return `/trips-list/planning/${tripId}`;
};

const extractFilterValue = (filterVal) => {
  if (filterVal === null || filterVal === undefined) return null;
  if (Array.isArray(filterVal)) {
    return filterVal.length > 0 ? filterVal : null;
  }
  if (typeof filterVal === "object") {
    // Date-range filters carry their value as { startDate, endDate } directly,
    // not something to unwrap further via .value/.label/.id.
    if ("startDate" in filterVal || "endDate" in filterVal) {
      return filterVal.startDate || filterVal.endDate ? filterVal : null;
    }
    const val = filterVal.value ?? filterVal.label ?? filterVal.id;
    if (val === null || val === undefined || val === "") return null;
    if (typeof val === "object") return extractFilterValue(val);
    return String(val).trim();
  }
  const str = String(filterVal).trim();
  return str === "" ? null : str;
};

const extractFilterType = (filterVal, defaultType = "contains") => {
  if (filterVal && typeof filterVal === "object" && filterVal.filter_type) {
    return filterVal.filter_type;
  }
  return defaultType;
};

// Shared by the listing fetch and report exports so they always reflect the same active filters.
// Exported so Trips.jsx (which embeds this component with hideHeader) can build the same payload
// for its own header's download buttons.
export const buildTripFilterPayload = (searchValue, searchFilters) => {
  const payload = {};
  if (searchValue && searchValue.trim() !== "") {
    payload.search_text = searchValue.trim();
  }

  if (searchFilters && typeof searchFilters === "object") {
    Object.keys(searchFilters).forEach((key) => {
      const rawFilter = searchFilters[key];
      const val = extractFilterValue(rawFilter);
      if (val !== null && val !== "") {
        const defaultType =
          key.toLowerCase().includes("status") || key.toLowerCase().includes("id")
            ? "equals"
            : "contains";
        const filter_type = extractFilterType(rawFilter, defaultType);
        const value = val;

        payload[key] = {
          filter_type:
            Array.isArray(value) && filter_type === defaultType
              ? "one_of"
              : filter_type,
          value,
        };
      }
    });
  }

  return payload;
};

const isTripDeletable = (t) => {
    const s = String(t?.status || '').toLowerCase().trim();
    const stg = String(t?.stage || '').toLowerCase().trim();
    if (s.includes('complete') || s.includes('close')) return false;
    if (stg.includes('complete') || stg.includes('close')) return false;
    return true;
};

const isTripResettable = (t) => {
    const s = String(t?.status || '').toLowerCase().trim();
    return s.includes('transit') || s.includes('complete') || s.includes('close');
};

const TripsList = ({
  hideHeader = false,
  refreshTrigger = 0,
  searchValue: searchValueProp,
  setSearchValue: setSearchValueProp,
  searchFilters: searchFiltersProp,
  setSearchFilters: setSearchFiltersProp,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const outletContext = useOutletContext();
  const { tripOverviewData } = useSelector((state) => state.trips);
  const { 
    usersDropdownData = [], 
    vehiclesDropdownData = [],
    costCenterDropdownData = [],
    customerDropdownData = []
  } = useSelector(
    (state) => state.dropdowns || state.dropdown || {},
  );

  const userMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(usersDropdownData) ? usersDropdownData : []).forEach((u) => {
      const id = String(u?.value ?? u?.id ?? "").trim();
      const name = String(u?.label ?? u?.name ?? u?.username ?? "").trim();
      if (id && name) {
        const cleanName = sanitizeDriverName(name);
        map.set(id, cleanName || name);
      }
    });
    return map;
  }, [usersDropdownData]);

  const userMapRef = useRef(userMap);
  const contractCacheRef = useRef(new Map());
  useEffect(() => {
    userMapRef.current = userMap;
  }, [userMap]);

  // Controlled by the parent when embedded (e.g. Trips.jsx's own header needs the same
  // search/filter state for its PDF Download button); falls back to internal state for the
  // standalone /trips-list route.
  const [internalSearchValue, setInternalSearchValue] = useState("");
  const [internalSearchFilters, setInternalSearchFilters] = useState({});
  const [orderBy, setOrderBy] = useState('created_at');
  const searchValue = searchValueProp !== undefined ? searchValueProp : internalSearchValue;
  const setSearchValue = setSearchValueProp || setInternalSearchValue;
  const searchFilters = searchFiltersProp !== undefined ? searchFiltersProp : internalSearchFilters;
  const setSearchFilters = setSearchFiltersProp || setInternalSearchFilters;

  // Export hooks — used by the standalone /trips-list header; Trips.jsx (hideHeader=true) has its own.
  const {
    hasActiveFilters: hasActiveListFilters,
    loading: pdfExportLoading,
    handleExport: handleExportPdf,
  } = useTripListingPdfExport(searchValue, searchFilters);
  const {
    loading: excelExportLoading,
    handleExport: handleExportExcel,
  } = useTripListingExcelExport(searchValue, searchFilters);
  const listExportLoading = pdfExportLoading || excelExportLoading;

  const [tripsData, setTripsData] = useState([]);
  const [tripStats, setTripStats] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [loadingTable, setLoadingTable] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Drawer State
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteReasonError, setDeleteReasonError] = useState("");

  // Reset Modal State
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [tripToReset, setTripToReset] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetReason, setResetReason] = useState("");
  const [resetReasonError, setResetReasonError] = useState("");

  // Cannot-Delete Info Modal State (for completed/closed trips)
  const [cannotDeleteModalOpen, setCannotDeleteModalOpen] = useState(false);
  const [cannotDeleteTrip, setCannotDeleteTrip] = useState(null);

  useEffect(() => {
    dispatch(fetchUsersDropdown());
    dispatch(fetchVehiclesDropdown());
    dispatch(fetchCostCenterDropdown());
    // Also use the native redux action for customer if it exists, otherwise keep local state fallback below
    dispatch(fetchCustomerDropdown());
  }, [dispatch]);

  // Customer Dropdown State fallback (if redux doesn't populate)
  const [customersDropdownData, setCustomersDropdownData] = useState([]);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await apiService.getCustomerDropdown();
        setCustomersDropdownData(
          response?.data?.results || response?.results || response?.data || [],
        );
      } catch (err) {
        console.error("Failed to fetch customers dropdown", err);
      }
    };
    fetchCustomers();
  }, []);

  // Stage Access / Permission Denied Modal State
  const [noPermissionModalOpen, setNoPermissionModalOpen] = useState(false);
  const [restrictedTripInfo, setRestrictedTripInfo] = useState({
    tripId: "",
    stageName: "",
  });

  // Permission Checks for Current User
  const canCreateTrip = useMemo(() => {
    return (
      isSuperAdminRole() ||
      hasPermission({
        permissionSlug: TripPermission.Create,
        menuSlug: TripSubModules.TripsList,
        moduleSlug: AppMainModules.tripManagement,
      }) ||
      hasPermission({
        permissionSlug: TripPermission.Create,
        menuSlug: TripSubModules.Trips,
        moduleSlug: AppMainModules.tripManagement,
      }) ||
      hasPermission({
        permissionSlug: TripPermission.Create,
        menuSlug: "trip-management",
        moduleSlug: AppMainModules.tripManagement,
      })
    );
  }, []);

  const canDeleteTrip = useMemo(() => {
    return (
      isSuperAdminRole() ||
      hasPermission({
        permissionSlug: TripPermission.Delete,
        menuSlug: TripSubModules.TripsList,
        moduleSlug: AppMainModules.tripManagement,
      }) ||
      hasPermission({
        permissionSlug: TripPermission.Delete,
        menuSlug: TripSubModules.Trips,
        moduleSlug: AppMainModules.tripManagement,
      }) ||
      hasPermission({
        permissionSlug: TripPermission.Delete,
        menuSlug: "trip-management",
        moduleSlug: AppMainModules.tripManagement,
      })
    );
  }, []);

  const canResetTrip = useMemo(() => hasTripPermission(TripPermission.ResetTrip), []);

  const canViewRouteJourney = useMemo(() => canViewTripRouteJourney(), []);

  // Handle Navigation on Trip ID Click
  const handleTripClick = useCallback(
    (trip) => {
      if (!trip) return;
      navigate(getTripDetailsRoute(trip));
    },
    [navigate],
  );

  const loadTripListing = useCallback(async () => {
    setLoadingTable(true);
    try {
      const payload = { ...buildTripFilterPayload(searchValue, searchFilters), order_by: orderBy };

      const res = await apiService.getTripListingPaginated(
        payload,
        currentPage,
        limit,
      );
      const results = res?.data?.results || res?.results || res?.data || [];
      const meta = res?.data?.meta || res?.meta || {};
      const stats = res?.data?.stats || res?.stats || null;

      if (stats) {
        setTripStats(stats);
      }

      const contractMap = {};
      const idsToFetch = [];
      (Array.isArray(results) ? results : []).forEach((item) => {
        const cId = item?.contract_id || item?.contract?.id;
        if (!cId) return;
        if (contractCacheRef.current.has(cId)) {
          contractMap[cId] = contractCacheRef.current.get(cId);
        } else if (!idsToFetch.includes(cId)) {
          idsToFetch.push(cId);
        }
      });

      if (idsToFetch.length > 0) {
        await Promise.all(
          idsToFetch.map(async (cId) => {
            try {
              const contractRes = await apiService.getContractTripDetail(cId);
              const vTypes =
                contractRes?.data?.vehicle_types ||
                contractRes?.vehicle_types ||
                [];
              contractCacheRef.current.set(cId, vTypes);
              contractMap[cId] = vTypes;
            } catch (e) {
              console.error(
                `Failed to fetch contract trip detail for contract ${cId}`,
                e,
              );
            }
          }),
        );
      }

      const formatted = (Array.isArray(results) ? results : [])
        .map((item) => {
          const cId = item?.contract_id || item?.contract?.id;
          const vTypes = contractMap[cId] || [];
          const vTypeId =
            item?.vehicle_type_id || item?.vehicle?.vehicle_type_id;
          let matchedVType = vTypes.find(
            (vt) => String(vt.id) === String(vTypeId),
          );
          if (!matchedVType && vTypes.length > 0) {
            matchedVType = vTypes[0];
          }

          const contractVehicleTypeTitle =
            matchedVType?.title || matchedVType?.name || null;
          return mapApiTripToTableItem(
            item,
            contractVehicleTypeTitle,
            userMapRef.current,
          );
        })
        .filter(Boolean);

      setTripsData(formatted);
      setTotalItems(meta?.totalItems ?? meta?.total ?? formatted.length);
    } catch (err) {
      console.error("Error loading trip listing:", err);
    } finally {
      setLoadingTable(false);
    }
  }, [currentPage, limit, searchValue, searchFilters, orderBy]);


  useEffect(() => {
    dispatch(fetchTripOverview());
    dispatch(fetchUsersDropdown());
    dispatch(fetchVehiclesDropdown());
  }, [dispatch]);

  useEffect(() => {
    if (!userMap || userMap.size === 0) return;
    setTripsData((prev) =>
      prev.map((trip) => {
        if (
          trip.driverName &&
          trip.driverName !== "Driver" &&
          trip.driverName !== "—"
        ) {
          return trip;
        }
        const dId = String(trip?.raw?.primary_driver_id || "");
        const mappedName = userMap.get(dId);
        if (mappedName && mappedName !== trip.driverName) {
          return { ...trip, driverName: sanitizeDriverName(mappedName) };
        }
        return trip;
      }),
    );
  }, [userMap]);

  useEffect(() => {
    loadTripListing();
  }, [loadTripListing]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      loadTripListing();
    }
  }, [refreshTrigger, loadTripListing]);

  // Handle Delete Confirmation & API Call
  const openDeleteConfirmation = (tripItem) => {
    const s = String(tripItem?.status || '').toLowerCase().trim();
    const isBlockedStatus = s.includes('complete') || s.includes('close');
    if (isBlockedStatus || tripItem?.has_invoice) {
      setCannotDeleteTrip(tripItem);
      setCannotDeleteModalOpen(true);
      return;
    }
    setTripToDelete(tripItem);
    setDeleteReason("");
    setDeleteReasonError("");
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tripToDelete) return;
    if (!deleteReason.trim() || deleteReason.trim().length < 5) {
      setDeleteReasonError("Reason is required (min 5 characters)");
      return;
    }
    const targetId = tripToDelete?.rawId || tripToDelete?.id;
    setDeleteLoading(true);
    try {
      await apiService.deleteTrip(targetId, deleteReason.trim());
      successNotify("Trip deleted successfully");
      setDeleteModalOpen(false);
      setTripToDelete(null);
      setDeleteReason("");
      loadTripListing();
      dispatch(fetchTripOverview());
    } catch (error) {
      console.error("Failed to delete trip:", error);
      errorNotify(error || "Failed to delete trip");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle Reset Confirmation & API Call
  const openResetConfirmation = (tripItem) => {
    setTripToReset(tripItem);
    setResetReason("");
    setResetReasonError("");
    setResetModalOpen(true);
  };

  const handleConfirmReset = async () => {
    if (!tripToReset) return;
    if (!resetReason.trim() || resetReason.trim().length < 5) {
      setResetReasonError("Reason is required (min 5 characters)");
      return;
    }
    const targetId = tripToReset?.rawId || tripToReset?.id;
    setResetLoading(true);
    try {
      await apiService.resetTripStatusToCreated(targetId, resetReason.trim());
      successNotify("Trip status reset to Created successfully");
      setResetModalOpen(false);
      setTripToReset(null);
      setResetReason("");
      loadTripListing();
      dispatch(fetchTripOverview());
    } catch (error) {
      console.error("Failed to reset trip status:", error);
      errorNotify(error || "Failed to reset trip status");
    } finally {
      setResetLoading(false);
    }
  };

  // Filter and Search Handler (Local Fallback Filter)
  const filteredTrips = useMemo(() => {
    return tripsData.filter((item) => {
      if (!item) return false;
      if (searchValue) {
        const q = searchValue.toLowerCase();
        const match =
          (item.id && String(item.id).toLowerCase().includes(q)) ||
          (item.customer && String(item.customer).toLowerCase().includes(q)) ||
          (item.driverName &&
            String(item.driverName).toLowerCase().includes(q)) ||
          (item.registrationNumber &&
            String(item.registrationNumber).toLowerCase().includes(q)) ||
          (item.status && String(item.status).toLowerCase().includes(q));
        if (!match) return false;
      }

      // Note: searchFilters are already applied server-side in loadTripListing
      // (tripsData already reflects them). Re-filtering here duplicated that
      // logic and broke multi-select ("one of") filters like Status/Stage.

      return true;
    });
  }, [tripsData, searchValue]);

  // Sorting Handler
  const sortedData = useMemo(() => {
    let result = [...filteredTrips];
    if (sortColumn && typeof sortColumn === "string") {
      result.sort((a, b) => {
        if (!a || !b) return 0;
        let aVal = a[sortColumn] || "";
        let bVal = b[sortColumn] || "";
        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [filteredTrips, sortColumn, sortDirection]);

  const handleSort = (column, direction) => {
    const colKey =
      typeof column?.selector === "string" ? column.selector : column?.name;
    setSortColumn(colKey || null);
    setSortDirection(direction);
  };

  const commonColumns = {
    checked: true,
    disabled: true,
    hide: false,
    exportColumn: true,
    sortable: false,
  };

  // Format dropdown options
  const vehicleDropdownOptions = useMemo(() => {
    return (vehiclesDropdownData || []).map((v) => ({
      label: v.registration_number || v.label || String(v.id || v.value),
      value: v.id ?? v.value,
    }));
  }, [vehiclesDropdownData]);

  const driverDropdownOptions = useMemo(() => {
    return (usersDropdownData || []).map((u) => ({
      label: u.username || u.name || u.label || String(u.id || u.value),
      value: u.id ?? u.value,
    }));
  }, [usersDropdownData]);

  const customerDropdownOptions = useMemo(() => {
    const data =
      (customerDropdownData?.length
        ? customerDropdownData
        : customersDropdownData) || [];
    return data.map((c) => ({
      label: c.name || c.label || String(c.id || c.value),
      value: c.id ?? c.value,
    }));
  }, [customerDropdownData, customersDropdownData]);

  const costCenterDropdownOptions = useMemo(() => {
    return (costCenterDropdownData || []).map((c) => ({
      label: c.name || c.label || String(c.id || c.value),
      value: c.id ?? c.value,
    }));
  }, [costCenterDropdownData]);

  // Columns Definition for NewTableComponent
  const columns = useMemo(() => {
    const baseCols = [
      {
        ...commonColumns,
        name: "Trip ID",
        selector: (row) => row?.id || "",
        cell: (row) => (
          <span
            className="hyperlink"
            role="button"
            tabIndex={0}
            onClick={() => handleTripClick(row)}
          >
            {row?.id}
          </span>
        ),
        tableColumn: true,
        filterName: "tripIdFilter",
        type: "text",
        minWidth: "160px",
      },
      {
        ...commonColumns,
        name: "Status",
        selector: (row) => row?.status || "",
        cell: (row) => renderTripStatusBadge(row?.status),
        tableColumn: true,
        filterName: "statusFilter",
        type: "select",
        options: [
          { label: "New", value: "new" },
          { label: "Active", value: "active" },
          { label: "In Transit", value: "in_transit" },
          { label: "Completed", value: "completed" },
          { label: "Closed", value: "closed" },
        ],
        minWidth: "130px",
      },
      {
        ...commonColumns,
        name: "Customer",
        selector: (row) => row?.customer || "",
        cell: (row) => (
          <span style={{ textTransform: "capitalize" }}>{row?.customer}</span>
        ),
        tableColumn: true,
        filterName: "customerIdFilter",
        type: "select",
        isMulti: true,
        filterComponent: CustomerFilterDropdown,
        options: customerDropdownOptions,
        minWidth: "210px",
      },
      {
        ...commonColumns,
        name: "Stage",
        selector: (row) => row?.stage || "",
        cell: (row) => renderTripStageBadge(row?.stage),
        tableColumn: true,
        filterName: "stageFilter",
        type: "select",
        options: [
          { label: "Created", value: "created" },
          { label: "Trip Stops", value: "trip-stops" },
          {
            label: "Checklists & Allocations",
            value: "checklists-and-allocations",
          },
          { label: "In Transit", value: "in_transit" },
          { label: "Correction", value: "correction" },
          { label: "Unverified", value: "unverified" },
          { label: "Verified", value: "verified" },
          { label: "Completed", value: "completed" },
          { label: "Closed", value: "closed" },
        ],
        minWidth: "160px",
      },

      ...(canViewRouteJourney
        ? [
            {
              ...commonColumns,
              name: "Route Progress",
              selector: (row) => row?.progress || 0,
              cell: (row) => (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    width: "100%",
                    padding: "4px 0",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#475569",
                      fontWeight: "500",
                    }}
                  >
                    {row?.origin} → {row?.destination}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        flex: 1,
                        height: "5px",
                        background: "#e2e8f0",
                        borderRadius: "3px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${row?.progress || 0}%`,
                          height: "100%",
                          background: "#1447b1",
                          borderRadius: "3px",
                        }}
                      ></div>
                    </div>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#64748b",
                        fontWeight: "500",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.progress}% - {row?.eta}
                    </span>
                  </div>
                </div>
              ),
              tableColumn: false,
              filterName: "routeFilter",
              type: "text",
              minWidth: "280px",
            },
          ]
        : []),
      {
        ...commonColumns,
        name: "Cost Center",
        selector: (row) => row?.costCenter || row?.raw?.cost_center?.name || "",
        cell: (row) => (
          <span>{row?.costCenter || row?.raw?.cost_center?.name || "—"}</span>
        ),
        tableColumn: true,
        filterName: "costCenterIdFilter",
        type: "select",
        isMulti: true,
        filterComponent: CostCenterFilterDropdown,
        options: costCenterDropdownOptions,
        minWidth: "160px",
      },
      {
        ...commonColumns,
        name: "Created Date",
        selector: (row) => row?.createdDate || "",
        cell: (row) => (
          <span style={{ color: "#475569", fontSize: "12px" }}>
            {row?.createdDate}
          </span>
        ),
        tableColumn: true,
        filterName: "createdAtFilter",
        type: "select",
        filterComponent: DateFilterDropdown,
        minWidth: "185px",
      },
      {
        ...commonColumns,
        name: "Start Date",
        selector: (row) => row?.startDate || "",
        cell: (row) => (
          <span style={{ color: "#475569", fontSize: "12px" }}>
            {row?.startDate}
          </span>
        ),
        tableColumn: true,
        filterName: "startAtFilter",
        type: "select",
        filterComponent: DateFilterDropdown,
        minWidth: "185px",
      },
      {
        ...commonColumns,
        name: "End Date",
        selector: (row) => row?.endDate || "",
        cell: (row) => (
          <span style={{ color: "#475569", fontSize: "12px" }}>
            {row?.endDate}
          </span>
        ),
        tableColumn: false,
        filterName: "endDateFilter",
        type: "date",
        minWidth: "185px",
      },
      {
        ...commonColumns,
        name: "Registration No",
        selector: (row) => row?.registrationNumber || row?.vehicleType || "",
        cell: (row) => <span>{row?.registrationNumber}</span>,
        tableColumn: true,
        filterName: "vehicleFilter",
        type: "select",
        isMulti: true,
        filterComponent: VehicleFilterDropdown,
        options: vehicleDropdownOptions,
        minWidth: "160px",
      },
      {
        ...commonColumns,
        name: "Vehicle Type",
        selector: (row) => formatVehicleTypeValue(row?.raw),
        cell: (row) => <span>{formatVehicleTypeValue(row?.raw)}</span>,
        tableColumn: false,
        minWidth: "130px",
      },
      {
        ...commonColumns,
        name: "TAT Days",
        selector: (row) => calculateTatDays(row?.raw?.start_at, row?.raw?.actual_end_at),
        cell: (row) => <span>{calculateTatDays(row?.raw?.start_at, row?.raw?.actual_end_at)}</span>,
        tableColumn: false,
        minWidth: "100px",
      },
      {
        ...commonColumns,
        name: "Start ODM",
        selector: (row) => formatNumberValue(row?.raw?.start_odm),
        cell: (row) => <span>{formatNumberValue(row?.raw?.start_odm)}</span>,
        tableColumn: false,
        minWidth: "125px",
      },
      {
        ...commonColumns,
        name: "End ODM",
        selector: (row) => formatNumberValue(row?.raw?.end_odm),
        cell: (row) => <span>{formatNumberValue(row?.raw?.end_odm)}</span>,
        tableColumn: false,
        minWidth: "125px",
      },
      {
        ...commonColumns,
        name: "Distance KM",
        selector: (row) => formatNumberValue(row?.raw?.total_odm),
        cell: (row) => <span>{formatNumberValue(row?.raw?.total_odm)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
      {
        ...commonColumns,
        name: "Allocated Fuel",
        selector: (row) => formatFuelValue(row?.raw?.allocated_fuel),
        cell: (row) => <span>{formatFuelValue(row?.raw?.allocated_fuel)}</span>,
        tableColumn: false,
        minWidth: "130px",
      },
      {
        ...commonColumns,
        name: "Total Fuel",
        selector: (row) => calculateTotalFuel(row?.raw),
        cell: (row) => <span>{calculateTotalFuel(row?.raw)}</span>,
        tableColumn: false,
        minWidth: "110px",
      },
      {
        ...commonColumns,
        name: "Utilized Fuel",
        selector: (row) => formatFuelValue(row?.raw?.utilized_fuel),
        cell: (row) => <span>{formatFuelValue(row?.raw?.utilized_fuel)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
      {
        ...commonColumns,
        name: "Balance Fuel",
        selector: (row) => calculateBalanceFuel(row?.raw),
        cell: (row) => <span>{calculateBalanceFuel(row?.raw)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
      {
        ...commonColumns,
        name: "KM/L",
        selector: (row) => calculateKmPerLiter(row?.raw),
        cell: (row) => <span>{calculateKmPerLiter(row?.raw)}</span>,
        tableColumn: false,
        minWidth: "80px",
      },
      {
        ...commonColumns,
        name: "Loading Point",
        selector: (row) => getLoadingPoint(row?.raw),
        cell: (row) => (
          <span
            title={getLoadingPoint(row?.raw)}
            style={{ maxWidth: "170px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }}
          >
            {getLoadingPoint(row?.raw)}
          </span>
        ),
        tableColumn: false,
        minWidth: "160px",
      },
      {
        ...commonColumns,
        name: "Decanting Point",
        selector: (row) => getDecantingPoint(row?.raw),
        cell: (row) => (
          <span
            title={getDecantingPoint(row?.raw)}
            style={{ maxWidth: "170px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block" }}
          >
            {getDecantingPoint(row?.raw)}
          </span>
        ),
        tableColumn: false,
        minWidth: "160px",
      },
      {
        ...commonColumns,
        name: "Assigned Driver",
        selector: (row) => row?.driverName || "",
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {row?.driverName && row?.driverName !== "—" && (
              <UserAvatar
                username={row?.driverName}
                image={row?.driverImage}
                size={28}
              />
            )}
            <span
              className={
                row?.driverName && row?.driverName !== "—" ? "hyperlink" : ""
              }
            >
              {row?.driverName || "—"}
            </span>
          </div>
        ),
        tableColumn: false,
        filterName: "driverFilter",
        type: "select",
        isMulti: true,
        filterComponent: DriverFilterDropdown,
        options: driverDropdownOptions,
        minWidth: "200px",
      },
      {
        ...commonColumns,
        name: "Contact Number",
        selector: (row) => row?.driverPhone || "",
        cell: (row) => (
          <span style={{ color: "#475569", fontSize: "12px" }}>
            {row?.driverPhone}
          </span>
        ),
        tableColumn: false,
        filterName: "phoneFilter",
        type: "text",
        minWidth: "140px",
      },
      {
        ...commonColumns,
        name: "Trip Revenue",
        selector: (row) => getTripRevenue(row),
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{getTripRevenue(row)}</span>,
        tableColumn: false,
        minWidth: "135px",
      },
      {
        ...commonColumns,
        name: "Fuel Expense",
        selector: (row) => getFuelExpense(row),
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{getFuelExpense(row)}</span>,
        tableColumn: false,
        minWidth: "135px",
      },
      {
        ...commonColumns,
        name: "Insurance Amt",
        selector: (row) => getInsuranceAmount(row),
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{getInsuranceAmount(row)}</span>,
        tableColumn: false,
        minWidth: "140px",
      },
      {
        ...commonColumns,
        name: "Shortage Amt",
        selector: (row) => getShortageAmount(row),
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{getShortageAmount(row)}</span>,
        tableColumn: false,
        minWidth: "140px",
      },
      {
        ...commonColumns,
        name: "Trip Expense",
        selector: (row) => getTripExpense(row),
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{getTripExpense(row)}</span>,
        tableColumn: false,
        minWidth: "135px",
      },
      {
        ...commonColumns,
        name: "Petty Cash",
        selector: (row) => getPettyCashAllocated(row),
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{getPettyCashAllocated(row)}</span>,
        tableColumn: false,
        minWidth: "130px",
      },
      {
        ...commonColumns,
        name: "Created User",
        selector: (row) => getCreatedUser(row),
        cell: (row) => <span>{getCreatedUser(row)}</span>,
        tableColumn: false,
        minWidth: "130px",
      },
      {
        ...commonColumns,
        name: "Created Date",
        selector: (row) => getCreatedDate(row),
        cell: (row) => <span style={{ whiteSpace: "nowrap" }}>{getCreatedDate(row)}</span>,
        tableColumn: false,
        minWidth: "200px",
      },
      {
        ...commonColumns,
        name: "Planned Qty",
        selector: (row) => row?.raw?.planned_qty ?? "",
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{formatNumberValue(row?.raw?.planned_qty)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
      {
        ...commonColumns,
        name: "Dispatch Qty",
        selector: (row) => row?.raw?.dispatch_qty ?? "",
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{formatNumberValue(row?.raw?.dispatch_qty)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
      {
        ...commonColumns,
        name: "Delivered Qty",
        selector: (row) => row?.raw?.delivered_qty ?? "",
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{formatNumberValue(row?.raw?.delivered_qty)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
      {
        ...commonColumns,
        name: "Shortage Qty",
        selector: (row) => row?.raw?.shortage_qty ?? "",
        cell: (row) => <span style={{ textAlign: "right", display: "block" }}>{formatNumberValue(row?.raw?.shortage_qty)}</span>,
        tableColumn: false,
        minWidth: "120px",
      },
    ];

    if (canDeleteTrip || canResetTrip) {
      baseCols.push({
        ...commonColumns,
        name: "Action",
        cell: (row) => {
          const showDelete = canDeleteTrip;
          const showReset = canResetTrip && isTripResettable(row);
          if (!showDelete && !showReset) return null;
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              {showDelete && (
                <button
                  className="table_action_delete_btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteConfirmation(row);
                  }}
                  title="Delete Trip">
                  <RiDeleteBinLine style={{ fontSize: "16px", color: "#ef4444" }} />
                </button>
              )}
              {showReset && (
                <button
                  className="table_action_reset_btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openResetConfirmation(row);
                  }}
                  title="Reset to New">
                  <MdOutlineRestartAlt style={{ fontSize: "17px", color: "#f59e0b" }} />
                </button>
              )}
            </div>
          );
        },
        tableColumn: false,
        minWidth: "100px",
      });
    }

    return baseCols;
  }, [
    canDeleteTrip,
    canResetTrip,
    canViewRouteJourney,
    handleTripClick,
    customerDropdownOptions,
    vehicleDropdownOptions,
    driverDropdownOptions,
    costCenterDropdownOptions,
  ]);


  const totalPagesCount = Math.max(
    Math.ceil((totalItems || sortedData.length) / limit),
    1,
  );

  return (
    <>
      {/* ========================================================= */}
      {/* 1. DESKTOP / WEB VIEW (Native Project NewTableComponent)  */}
      {/* ========================================================= */}
      <div
        className={`desktop_view_only ${hideHeader ? "trips_list_embedded" : "trip_page"}`}
      >
        {!hideHeader && (
          <>
            <Breadcrumbs values={["Trip List Management"]} />

            <div className="access_view">
              <h2>Trip List Management</h2>

              <div className="services_buttons">
                <Notification />

                <ExportButton
                  type="pdf"
                  onClick={handleExportPdf}
                  disabled={!hasActiveListFilters || listExportLoading}
                  loading={pdfExportLoading}
                  hasActiveFilters={hasActiveListFilters}
                />

                <ExportButton
                  type="excel"
                  onClick={handleExportExcel}
                  disabled={!hasActiveListFilters || listExportLoading}
                  loading={excelExportLoading}
                  hasActiveFilters={hasActiveListFilters}
                />

                {canCreateTrip && (
                  <button onClick={() => setIsCreateDrawerOpen(true)}>
                    {plusIcon} Create New Trip
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* Stat Cards Row */}
        <TripsListStats overviewData={tripStats || tripOverviewData} />

        {/* Project Native NewTableComponent */}
        <div className="trips_table_main_wrapper">
          <div className="trips_sort_toggle_row">
            <span className="trips_sort_label">Sort by:</span>
            <div className="trips_sort_btn_group">
              <button
                type="button"
                className={`trips_sort_btn ${orderBy === 'created_at' ? 'active' : ''}`}
                onClick={() => setOrderBy('created_at')}
              >
                Created Date
              </button>
              <button
                type="button"
                className={`trips_sort_btn ${orderBy === 'start_at' ? 'active' : ''}`}
                onClick={() => setOrderBy('start_at')}
              >
                Start Date
              </button>
            </div>
          </div>

          <NewTableComponent
            columns={columns}
            data={sortedData}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            limit={limit}
            setLimit={setLimit}
            totalItems={totalItems || sortedData.length}
            totalPages={totalPagesCount}
            onSearch={(val) => setSearchValue(val)}
            searchValue={searchValue}
            setSearchFilters={setSearchFilters}
            searchFilters={searchFilters}
            onSort={handleSort}
            showFilter={true}
            showAddBtn={false}
            btnName="Create New Trip"
            onAddClick={() => setIsCreateDrawerOpen(true)}
            loading={loadingTable}
          />
        </div>
      </div>

      {/* ========================================================= */}
      {/* 2. MOBILE RESPONSIVE VIEW (100% Exact Match Screenshot 1) */}
      {/* ========================================================= */}
      <div className="mobile_view_only trips_list_container">
        {!hideHeader && (
          <TripsListHeader
            onOpenCreateTrip={() => setIsCreateDrawerOpen(true)}
            onToggleMobileSidebar={outletContext?.toggleMobileSidebar}
          />
        )}

        {/* Stat Cards Grid */}
        <TripsListStats overviewData={tripStats || tripOverviewData} />

        {/* Mobile Search Bar */}
        <div className="mobile_search_bar_wrapper">
          <div className="mobile_search_input_box">
            <FiSearch className="search_icon_svg" />
            <input
              type="text"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
        </div>

        <MobileListingFilters
          columns={columns}
          searchFilters={searchFilters}
          setSearchFilters={setSearchFilters}
        />

        {/* Mobile Cards List */}
        <div className="mobile_trip_cards_list">
          {sortedData.map((trip) => (
            <div className="mobile_trip_card_item" key={trip.id}>
              {/* Top Row: ID, Vehicle, Stage & Status Badges */}
              <div className="mobile_card_top_bar">
                <div className="mobile_card_left_info">
                  <span
                    className="mobile_trip_id"
                    onClick={() => handleTripClick(trip)}
                    style={{ cursor: "pointer" }}
                  >
                    {trip.id}
                  </span>
                  <span className="mobile_vehicle_info">
                    {trip?.registrationNumber || trip?.vehicleModel}
                  </span>
                </div>

                <div className="mobile_card_right_badges">
                  {trip?.stage && (
                    <div className="mobile_trip_state_line">
                      <span className="mobile_state_label">Stage</span>
                      {renderTripStageBadge(trip.stage)}
                    </div>
                  )}
                  <div className="mobile_trip_state_line">
                    <span className="mobile_state_label">Status</span>
                    {renderTripStatusBadge(trip?.status)}
                  </div>
                </div>
              </div>

              {/* Customer Name */}
              <div className="mobile_customer_name_row">
                <span className="mobile_customer_label">Customer:</span>
                <span className="mobile_customer_val">
                  {trip?.customer || "—"}
                </span>
              </div>

              {canViewRouteJourney && (
                <>
                  {/* Route Text */}
                  <div className="mobile_route_text_row">
                    <FiNavigation className="mobile_nav_icon_svg" />
                    <span>
                      {trip.origin} → {trip.destination}
                    </span>
                  </div>

                  {/* Distance Progress Bar Line */}
                  <div className="mobile_progress_wrapper">
                    <div className="mobile_progress_bar_bg">
                      <div
                        style={{
                          height: "100%",
                          width: `${trip.progress}%`,
                          backgroundColor: "#1447b1",
                          borderRadius: "3px",
                          transition: "width 0.3s ease",
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* ETA Subtext */}
                  <div className="mobile_eta_text">
                    {trip.progress}% - {trip.eta}
                  </div>
                </>
              )}

              {/* Divider Line */}
              <div
                style={{
                  height: "1px",
                  backgroundColor: "#f1f5f9",
                  width: "100%",
                  margin: "10px 0",
                }}
              ></div>

              {/* Start / End Dates Row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "11px",
                  color: "#64748b",
                  marginBottom: "8px",
                  flexWrap: "wrap",
                  gap: "4px",
                }}
              >
                <span style={{ fontWeight: "500", color: "#475569" }}>
                  Schedule:
                </span>
                <span style={{ fontWeight: "600", color: "#334155" }}>
                  {trip?.startDate || "—"} → {trip?.endDate || "—"}
                </span>
              </div>

              {/* Bottom Row: Driver Info & Delete Action */}
              <div className="mobile_card_bottom_row">
                <div className="mobile_driver_info_box">
                  {trip?.driverName && trip?.driverName !== "—" && (
                    <UserAvatar
                      username={trip?.driverName}
                      image={trip?.driverImage}
                      size={32}
                    />
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                  >
                    <div className="mobile_driver_name_text">
                      {trip.driverName || "—"}
                    </div>
                    <div className="mobile_driver_phone_text">
                      {trip.driverPhone}
                    </div>
                  </div>
                </div>

                {canDeleteTrip || (canResetTrip && isTripResettable(trip)) ? (
                  <div style={{ display: "flex", gap: "6px" }}>
                    {canDeleteTrip && (
                      <button
                        className="mobile_delete_icon_btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteConfirmation(trip);
                        }}
                        title="Delete Trip"
                      >
                        <RiDeleteBinLine style={{ fontSize: "18px", color: "#ef4444" }} />
                      </button>
                    )}
                    {canResetTrip && isTripResettable(trip) && (
                      <button
                        className="mobile_delete_icon_btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openResetConfirmation(trip);
                        }}
                        title="Reset to New"
                      >
                        <MdOutlineRestartAlt style={{ fontSize: "19px", color: "#f59e0b" }} />
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* Pixel-Perfect Responsive Mobile Pagination Bar */}
        <div className="mobile_pagination_container">
          <div className="mobile_pagination_top_row">
            <span className="mobile_pagination_text">
              Total {totalItems || sortedData.length} items
            </span>
            <select
              className="mobile_limit_select"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={5}>5 / page</option>
              <option value={8}>8 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
            </select>
          </div>

          <div className="mobile_pagination_nav_row">
            <button
              className="mobile_nav_btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              title="Previous Page"
            >
              <FiChevronLeft size={16} /> Prev
            </button>
            <span className="mobile_page_info">
              Page {currentPage} of {totalPagesCount}
            </span>
            <button
              className="mobile_nav_btn"
              disabled={currentPage >= totalPagesCount}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPagesCount))
              }
              title="Next Page"
            >
              Next <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* ========================================================= */}
      {/* 3. CREATE NEW TRIP SLIDING DRAWER (Web Right Drawer / Mobile)*/}
      {/* ========================================================= */}
      <CreateTripDrawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        onTripCreated={() => loadTripListing()}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={deleteModalOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteModalOpen(false);
            setTripToDelete(null);
            setDeleteReason("");
            setDeleteReasonError("");
          }
        }}
        onConfirm={handleConfirmDelete}
        loading={deleteLoading}
        title={`Delete Trip ${tripToDelete?.id ? `(${tripToDelete.id})` : ""}?`}
        description="Are you sure you want to delete this trip? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      >
        <div className="confirm_delete_reason_wrap">
          <label className="confirm_delete_reason_label">
            Reason<span>*</span>
          </label>
          <textarea
            className={`confirm_delete_reason_textarea${deleteReasonError ? " is-error" : ""}`}
            placeholder="Enter reason for deletion (min 5 characters)"
            value={deleteReason}
            onChange={(e) => {
              setDeleteReason(e.target.value);
              if (deleteReasonError) setDeleteReasonError("");
            }}
            disabled={deleteLoading}
          />
          {deleteReasonError && (
            <span className="confirm_delete_reason_error">{deleteReasonError}</span>
          )}
        </div>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        show={resetModalOpen}
        onClose={() => {
          if (!resetLoading) {
            setResetModalOpen(false);
            setTripToReset(null);
            setResetReason("");
            setResetReasonError("");
          }
        }}
        onConfirm={handleConfirmReset}
        loading={resetLoading}
        title={`Reset Trip ${tripToReset?.id ? `(${tripToReset.id})` : ""} to New?`}
        description="This will reset the trip status back to New. This action cannot be undone."
        confirmText="Reset"
        cancelText="Cancel"
      >
        <div className="confirm_delete_reason_wrap">
          <label className="confirm_delete_reason_label">
            Reason<span>*</span>
          </label>
          <textarea
            className={`confirm_delete_reason_textarea${resetReasonError ? " is-error" : ""}`}
            placeholder="Enter reason for reset (min 5 characters)"
            value={resetReason}
            onChange={(e) => {
              setResetReason(e.target.value);
              if (resetReasonError) setResetReasonError("");
            }}
            disabled={resetLoading}
          />
          {resetReasonError && (
            <span className="confirm_delete_reason_error">{resetReasonError}</span>
          )}
        </div>
      </ConfirmDeleteModal>

      {/* Cannot Delete Modal — for Completed/Closed trips or invoiced trips */}
      <ConfirmDeleteModal
        show={cannotDeleteModalOpen}
        onClose={() => { setCannotDeleteModalOpen(false); setCannotDeleteTrip(null); }}
        onConfirm={
          !cannotDeleteTrip?.has_invoice && canResetTrip
            ? () => {
                setCannotDeleteModalOpen(false);
                const trip = cannotDeleteTrip;
                setCannotDeleteTrip(null);
                openResetConfirmation(trip);
              }
            : () => { setCannotDeleteModalOpen(false); setCannotDeleteTrip(null); }
        }
        title="Cannot Delete This Trip"
        description={
          cannotDeleteTrip?.has_invoice
            ? `An invoice has been generated for trip ${cannotDeleteTrip?.id}. It cannot be deleted or reset.`
            : `Trip ${cannotDeleteTrip?.id} has status ${cannotDeleteTrip?.status} and cannot be deleted directly. Please reset the trip first, then you can delete it.`
        }
        confirmText={!cannotDeleteTrip?.has_invoice && canResetTrip ? "Reset Trip" : "Close"}
        cancelText={!cannotDeleteTrip?.has_invoice && canResetTrip ? "Close" : null}
      />

      {/* No Permission Modal */}
      <NoPermissionModal
        show={noPermissionModalOpen}
        onClose={() => setNoPermissionModalOpen(false)}
        title="Access Restricted"
        message="You do not have permission to access this trip stage. Please contact your system administrator to request access."
        tripId={restrictedTripInfo.tripId}
        stageName={restrictedTripInfo.stageName}
      />
    </>
  );
};

export default TripsList;
